// Starter project templates for the planner. Each creates a set of task cards
// in the "Planning" column. `calculatorType` links a task to a calculator so the
// Buy Sheet can suggest the right companion materials. *Fr fields = fr-CA.
import type { TaskCategory, Column } from '../lib/storage'
import type { Locale } from '../i18n/locale'

export interface TemplateTask {
  name: string
  nameFr: string
  category: TaskCategory
  calculatorType?: string  // calculator slug, enables Buy Sheet companion hints
  column?: Column          // defaults to 'planning'
}

export interface ProjectTemplate {
  id: string
  name: string
  nameFr: string
  icon: string
  description: string
  descriptionFr: string
  tasks: TemplateTask[]
}

export const PROJECT_TEMPLATES: ProjectTemplate[] = [
  {
    id: 'bathroom',
    name: 'Bathroom Renovation',
    nameFr: 'Rénovation de salle de bain',
    icon: '🛁',
    description: 'Full bath remodel — demo through fixtures.',
    descriptionFr: 'Réfection complète — de la démolition aux appareils.',
    tasks: [
      { name: 'Demolition & haul-away', nameFr: 'Démolition et évacuation', category: 'structural' },
      { name: 'Plumbing rough-in', nameFr: 'Plomberie (installation brute)', category: 'plumbing' },
      { name: 'Backer board & waterproofing', nameFr: 'Panneau d’appui et étanchéité', category: 'tile', calculatorType: 'tile' },
      { name: 'Floor tile', nameFr: 'Carrelage de plancher', category: 'tile', calculatorType: 'tile' },
      { name: 'Shower & wall tile', nameFr: 'Carrelage de douche et de mur', category: 'tile', calculatorType: 'tile' },
      { name: 'Vanity, sink & faucet', nameFr: 'Meuble-lavabo, lavabo et robinet', category: 'bathroom' },
      { name: 'Paint walls & ceiling', nameFr: 'Peindre murs et plafond', category: 'paint', calculatorType: 'paint' },
      { name: 'Lighting & exhaust fan', nameFr: 'Éclairage et ventilateur', category: 'electrical' },
      { name: 'Fixtures & accessories', nameFr: 'Appareils et accessoires', category: 'bathroom' },
    ],
  },
  {
    id: 'kitchen',
    name: 'Kitchen Renovation',
    nameFr: 'Rénovation de cuisine',
    icon: '🍳',
    description: 'Cabinets, counters, backsplash and more.',
    descriptionFr: 'Armoires, comptoirs, dosseret et plus.',
    tasks: [
      { name: 'Demolition', nameFr: 'Démolition', category: 'structural' },
      { name: 'Plumbing (sink & dishwasher)', nameFr: 'Plomberie (évier et lave-vaisselle)', category: 'plumbing' },
      { name: 'Electrical & lighting', nameFr: 'Électricité et éclairage', category: 'electrical' },
      { name: 'Cabinets', nameFr: 'Armoires', category: 'kitchen' },
      { name: 'Countertops', nameFr: 'Comptoirs', category: 'kitchen' },
      { name: 'Backsplash tile', nameFr: 'Carrelage de dosseret', category: 'tile', calculatorType: 'tile' },
      { name: 'Flooring', nameFr: 'Plancher', category: 'flooring', calculatorType: 'flooring' },
      { name: 'Paint walls & trim', nameFr: 'Peindre murs et moulures', category: 'paint', calculatorType: 'paint' },
      { name: 'Appliances', nameFr: 'Électroménagers', category: 'kitchen' },
    ],
  },
  {
    id: 'basement',
    name: 'Basement Finishing',
    nameFr: 'Aménagement de sous-sol',
    icon: '🏚️',
    description: 'Turn an unfinished basement into living space.',
    descriptionFr: 'Transformer un sous-sol non fini en espace habitable.',
    tasks: [
      { name: 'Frame walls', nameFr: 'Charpente des murs', category: 'structural' },
      { name: 'Electrical rough-in', nameFr: 'Électricité (installation brute)', category: 'electrical' },
      { name: 'Insulation', nameFr: 'Isolation', category: 'structural', calculatorType: 'insulation' },
      { name: 'Drywall', nameFr: 'Gypse', category: 'drywall', calculatorType: 'drywall' },
      { name: 'Flooring', nameFr: 'Plancher', category: 'flooring', calculatorType: 'flooring' },
      { name: 'Paint', nameFr: 'Peinture', category: 'paint', calculatorType: 'paint' },
      { name: 'Drop ceiling', nameFr: 'Plafond suspendu', category: 'structural' },
      { name: 'Egress window', nameFr: 'Fenêtre de secours', category: 'structural' },
    ],
  },
  {
    id: 'repaint',
    name: 'Whole-House Repaint',
    nameFr: 'Repeindre toute la maison',
    icon: '🎨',
    description: 'Refresh every room, inside and out.',
    descriptionFr: 'Rafraîchir chaque pièce, intérieur et extérieur.',
    tasks: [
      { name: 'Prep, patch & sand', nameFr: 'Préparer, réparer et sabler', category: 'paint' },
      { name: 'Prime bare/patched areas', nameFr: 'Apprêter les zones nues/réparées', category: 'paint', calculatorType: 'paint' },
      { name: 'Ceilings', nameFr: 'Plafonds', category: 'paint', calculatorType: 'paint' },
      { name: 'Walls — main floor', nameFr: 'Murs — rez-de-chaussée', category: 'paint', calculatorType: 'paint' },
      { name: 'Walls — bedrooms', nameFr: 'Murs — chambres', category: 'paint', calculatorType: 'paint' },
      { name: 'Trim, doors & baseboards', nameFr: 'Moulures, portes et plinthes', category: 'paint' },
      { name: 'Exterior', nameFr: 'Extérieur', category: 'paint', calculatorType: 'paint' },
    ],
  },
  {
    id: 'deck',
    name: 'Deck Build',
    nameFr: 'Construction de terrasse',
    icon: '🪵',
    description: 'Build a new backyard deck from footings up.',
    descriptionFr: 'Construire une nouvelle terrasse, des semelles vers le haut.',
    tasks: [
      { name: 'Layout & permits', nameFr: 'Implantation et permis', category: 'outdoor' },
      { name: 'Footings & concrete', nameFr: 'Semelles et béton', category: 'concrete', calculatorType: 'concrete' },
      { name: 'Framing & joists', nameFr: 'Charpente et solives', category: 'outdoor', calculatorType: 'deck' },
      { name: 'Decking boards', nameFr: 'Planches de terrasse', category: 'outdoor', calculatorType: 'deck' },
      { name: 'Railings & balusters', nameFr: 'Garde-corps et barreaux', category: 'outdoor' },
      { name: 'Stairs', nameFr: 'Escalier', category: 'outdoor' },
      { name: 'Seal or stain', nameFr: 'Sceller ou teindre', category: 'outdoor' },
    ],
  },
  {
    id: 'flooring',
    name: 'New Flooring',
    nameFr: 'Nouveau plancher',
    icon: '🪜',
    description: 'Replace flooring in one or more rooms.',
    descriptionFr: 'Remplacer le plancher dans une ou plusieurs pièces.',
    tasks: [
      { name: 'Remove old flooring', nameFr: 'Enlever l’ancien plancher', category: 'flooring' },
      { name: 'Subfloor repair & prep', nameFr: 'Réparer et préparer le sous-plancher', category: 'flooring' },
      { name: 'Underlayment', nameFr: 'Sous-couche', category: 'flooring', calculatorType: 'flooring' },
      { name: 'Install flooring', nameFr: 'Poser le plancher', category: 'flooring', calculatorType: 'flooring' },
      { name: 'Transitions & thresholds', nameFr: 'Transitions et seuils', category: 'flooring' },
      { name: 'Baseboards & trim', nameFr: 'Plinthes et moulures', category: 'flooring' },
    ],
  },
]

export const templateName = (tpl: ProjectTemplate, locale: Locale) => (locale === 'fr' ? tpl.nameFr : tpl.name)
export const templateDesc = (tpl: ProjectTemplate, locale: Locale) => (locale === 'fr' ? tpl.descriptionFr : tpl.description)
export const templateTaskName = (task: TemplateTask, locale: Locale) => (locale === 'fr' ? task.nameFr : task.name)
